import * as React from 'react'
import { createContext, PropsWithChildren, ReactElement, useContext, useEffect, useMemo, useState } from 'react'
import { AxiosInstance } from 'axios'
import useEntity from '../hooks/useEntity'
import AuthorizationContext from './AuthorizationContext'
import { UserListItemDto } from '../config/userConfig'
import { DocumentContext, DocumentType, IDefaultFolderDto } from '../config/documentConfig'
import { FilePartyRole } from '../config/filePartyConfig'
import { DimensionsDto } from '../v2/components/Form/Accounting/DimensionFields'
import { LegalAidCertificateSettings } from '../components/Form/LegalAidCertificate/LegalAidCertificateForm'
import { AuthorizationGroupDto } from '../components/Combobox/AuthorizationGroupCombobox'
import { Country, Currency, I18nNomenclature, Language, LegalStructure, SurchargeType, VatType } from '../config/i18nConfig'
import { AttributeDefinitionDto } from '../hooks/useAttributes'
import { DisbursementType, FixedPriceType } from '../config/registrationConfig'
import { Billable } from '../config/registration/timeConfig'
import { Gender } from '../config/contactConfig'
import { TaskDateSettings } from '../helpers/taskDateHelper'
import { InvoiceMessage } from '../config/finance/invoiceConfig'
import { WorkflowDto } from '../components/TagPicker/WorkflowPicker'
import { EntityType } from '../v2/components/Form/Accounting/EntityField'
import { OrganisationProfileDto } from '../components/Combobox/OrganisationProfileCombobox'
import useSharePoint from '../hooks/useSharePoint'
import useAxios from '../hooks/useAxios'
import { DriveItem } from '@microsoft/microsoft-graph-types'
import { Destination, IGraphArrayPayload } from './AxiosContext'

export enum ModuleType {
    Accounting = 'ACCOUNTING',
    AIAgent = 'AI_AGENT',
    Attributes = 'ATTRIBUTES',
    Authorization = 'AUTHORIZATION',
    AuditTrail = 'AUDIT_TRAIL',
    AuditTrailTracking = 'AUDIT_TRAIL_TRACKING',
    BudgetMonitoring = 'BUDGET_MONITORING',
    CRM = 'CRM',
    Disbursements = 'DISBURSEMENTS',
    Docket = 'DOCKET',
    FamilyLaw = 'FAMILY_LAW',
    FixedPrices = 'FIXED_PRICES',
    Injury = 'INJURY',
    Invoices = 'INVOICES',
    InvoiceApproval = 'INVOICE_APPROVAL',
    Insolvency = 'INSOLVENCY',
    LegalAid = 'LEGAL_AID',
    Mollie = 'MOLLIE',
    ProofMe = 'PROOF_ME',
    Reports = 'REPORTS',
    ReportsPro = 'REPORTS_PRO',
    Tasks = 'TASKS',
    TimeCalendar = 'TIME_CALENDAR',
    TimeCategories = 'TIME_CATEGORIES',
    TimeTracking = 'TIME_TRACKING',
    TravelExpenses = 'TRAVEL_EXPENSES',
    Workflow = 'WORKFLOW',
    WorkInProgress = 'WORK_IN_PROGRESS',
}

export type ModuleDto = {
    type: ModuleType
    fileModule: boolean
    defaultActiveForFile: boolean
}

export enum Accounting {
    Exact = 'EXACT',
    Twinfield = 'TWINFIELD',
}

export type AIAgentDto = {
    enabled: boolean
    agents: AIAgentEnum[]
}

export enum AIAgentEnum {
    LegalPa = 'LEGALPA',
}

export type DashboardDto = {
    enabled?: boolean
    showRecentFiles?: boolean
}

export type DocumentDto = {
    uri: string
    label: string
    type: DocumentType
    version?: number
    context: DocumentContext
    name?: string
    folder?: string //can be any string. Is matched against IDefaultFolderDto#type. HAMMOCK uses FolderType, but custom
    role?: FilePartyRole | string
    // HAM-4167: absent means the Azure template mount; for SHAREPOINT the uri is the path within the drive.
    storage?: 'AZURE' | 'SHAREPOINT'
    driveId?: string
}

export enum DocumentOpenType {
    Open = 'OPEN',
    Download = 'DOWNLOAD',
}

// When opening a document (HAM-4251): APP = desktop app protocol handler (e.g. ms-word:),
// ONLINE = the Office Online web url (driveItem.webUrl). Used by users on Microsoft Online only.
export enum DocumentOpenTarget {
    App = 'APP',
    Online = 'ONLINE',
}

// The DOCUMENTS_OPEN setting: action decides open vs. download, target decides — when opening —
// desktop app vs. Office Online. Both optional; consumers fall back to DEFAULT_DOCUMENTS_OPEN.
export type DocumentsOpen = {
    action?: DocumentOpenType
    target?: DocumentOpenTarget
}

export type DocumentsSharing = {
    enabled?: boolean
    name: string
    driveId: string
}

export type FilePartyRoleDto = {
    type: FilePartyRole | string
    label: string
}

export enum FileRateType {
    File = 'FILE',
    PartyTier = 'PARTY_TIER',
    UserTier = 'USER_TIER',
}

export type FileScaffoldAuthorizationDto = {
    individuals: string[]
    groups: string[]
}

export type FileScaffoldDto = {
    accounting: DimensionsDto
    authorization: FileScaffoldAuthorizationDto
    billable: Billable
    currency: string
    fileRate: FileRateDto
    language: string
    paymentCurrency: string
    unit: string
    vatType: string
}

export type FileRateDto = {
    type: FileRateType
    hourRate: string
    surcharges?: string[]
}

export enum SortDirection {
    Ascending = 'ASC',
    Descending = 'DESC',
}

export type FileSortDto = {
    direction: SortDirection
    field: string
}

export type UnitDto = {
    key: string
    name: string
    enabled: boolean
    accounting: Accounting
    dimensions: EntityType[]
}

export type GlobalContextType = {
    settings?: SettingsDto
    attributeDefinitions?: AttributeDefinitionDto[]
    authorizationGroups?: AuthorizationGroupDto[]
    countries?: Country[]
    currencies?: Currency[]
    disbursementTypes?: DisbursementType[]
    fixedPriceTypes?: FixedPriceType[]
    languages?: Language[]
    legalStructures?: LegalStructure[]
    organisationProfiles?: OrganisationProfileDto[]
    surchargeTypes?: SurchargeType[]
    taskStatuses?: TaskStatus[]
    units?: UnitDto[]
    users?: UserListItemDto[]
    vatTypes?: VatType[]
    workflows?: WorkflowDto[]
    loaded: boolean
    reset?: () => void
}

export type InvoiceRecipientDto = {
    clients?: string[]
    debtors?: string[]
}

export type InsolvencySetting = {
    factorEstate: string
    rateEstate: string
    roles?: InsolvencyRole[]
}

type InsolvencyRole = {
    key: string
    name: string
}

export type LocalizedValueSetting<K = string, V = string> = {
    key: K
    value: V
}

export type LegalPADto = {
    organisationId: string
    consentComplete: boolean
}

export type TaskConfiguration = TaskDateSettings & {
    defaultTag?: string
    categories?: TaskCategory[]
}

export type TaskCategory = {
    key: string
    name: string
    color?: string
    isDefault?: boolean
    description?: string
}

export type TaskStatus = I18nNomenclature & {
    alpha: string
    isStartStatus?: boolean
    isEndStatus?: boolean
}

export enum ThemeOption {
    Light = 'LIGHT',
    Dark = 'DARK',
    Auto = 'AUTO',
}

export enum TimeSuggestionDescriptionOptionEnum {
    EmailIn = 'EMAIL_IN',
    EmailOut = 'EMAIL_OUT',
}

export enum TimeCategoryDefaultEnum {
    DefaultEmailIn = 'DEFAULT_EMAIL_IN',
    DefaultEmailOut = 'DEFAULT_EMAIL_OUT',
    Default = 'DEFAULT',
}

export type TimeCategory = {
    key: string
    name: string
    module?: ModuleType
    defaultDescription?: string
    appendDescription?: boolean
    defaultType?: TimeCategoryDefaultEnum
    billable?: Billable
}

// Everything the office configures about time writing (HAM-4354). Shaped like TaskConfiguration above: the
// categories that used to be their own timeCategories setting, plus the tag the time registration overview
// opens on. Can be overridden per user; the server merges such an override field by field over the office
// value, so a user setting only carries what differs.
export type TimeConfiguration = {
    categories?: TimeCategory[]
    // The tag the interval filter of the time overview opens on, passed through as-is: 'lastweek', 'week',
    // 'month', 'all' or a weekday ('monday'..'sunday'). Empty means the current day. The value is deliberately
    // not validated, so a tag the filter does not know gives an overview without date bounds and a button
    // showing the raw translation key - the office has to enter a tag that exists.
    defaultTag?: string
}

export enum TimeSuggestionOptionEnum {
    None = 'NONE',
    Notification = 'NOTIFICATION',
    Popup = 'POPUP',
}

export type TimeSuggestionDto = {
    fileSingleEmail?: TimeSuggestionOptionEnum
    fileMultipleEmail?: TimeSuggestionOptionEnum
    incomingEmail?: TimeSuggestionOptionEnum
    sentEmail?: TimeSuggestionOptionEnum
}

export type SettingsDto = {
    modules?: ModuleDto[]
    aiAgent?: AIAgentDto
    cocNumber?: number
    crmNoDefaultFile?: boolean
    dashboard?: DashboardDto
    documents?: DocumentDto[]
    documentsOpen?: DocumentsOpen
    documentsSharing?: DocumentsSharing
    // Environment-wide default sort order of the document screen (a DocumentSortOrder value,
    // e.g. "ModifiedDesc"); a user's own choice (localStorage) wins over this default (HAM-4057).
    documentsSort?: string
    fileExchangeDisplayName?: string
    fileSharepointDisplayName?: string
    filePartyRoles?: FilePartyRoleDto[]
    fileReference?: any
    fileScaffold?: FileScaffoldDto
    fileSort?: FileSortDto
    folders?: IDefaultFolderDto[]
    ibanNumber?: string
    invoiceEmailBcc?: string[]
    invoiceEmailMessages?: InvoiceMessage[]
    invoicePaymentCurrency?: boolean
    invoicePaymentMessages?: InvoiceMessage[]
    invoiceRecipient?: InvoiceRecipientDto
    invoiceNumber?: any
    insolvency?: InsolvencySetting
    legalAidCertificate?: LegalAidCertificateSettings
    legalpa?: LegalPADto
    locality?: string
    // Office-wide order of the main-menu buttons (a list of MenuItemKey values, HAM-4253).
    // Empty/undefined = default order. Buttons not listed keep their default order after the
    // listed ones; unknown/hidden keys are ignored. Applied in MainMenu.tsx.
    menuOrder?: string[]
    partySalutation?: LocalizedValueSetting<string, LocalizedValueSetting<Gender>[]>[]
    partyAttentionOf?: LocalizedValueSetting<string, LocalizedValueSetting<Gender>[]>[]
    task?: TaskConfiguration
    termInvoice?: number
    termPayment?: number
    // Time writing configuration: the categories and the default period of the overview (HAM-4354). Can be overridden per user.
    time?: TimeConfiguration
    // Colouring of the time writing calendar (HAM-3622). Can be overridden per user.
    timeCalendar?: TimeCalendarDto
    timeDivisor?: number
    timeSuggestion?: TimeSuggestionDto
    timeSuggestionDescription?: LocalizedValueSetting<string, LocalizedValueSetting<TimeSuggestionDescriptionOptionEnum>[]>[]
    timeSuggestionNotificationsDays?: number
    // Organisation-wide kilometre rate as validity periods (HAM-3627); resolved per travel date
    // by resolveTravelRate (src/helpers/travelRateHelper.ts).
    travelRate?: TravelRatePeriodDto[]
    vatNumber?: string
}

export type TimeCalendarDto = {
    // Switches the colour scheme as a whole; false means no colouring and no switch at all. A row written
    // before HAM-4315 has no value, which counts as enabled so an untouched office keeps its colours.
    colorsEnabled?: boolean
    // When true the user cannot switch the colouring off.
    colorsRequired?: boolean
    // Boundary in minutes between the orange and green colouring of a workday.
    dayThresholdMinutes?: number
    // Lists the hours norm in the hover popover of the month summary; independent of the colouring. A row
    // written before HAM-4315 has no value, which counts as shown.
    showNorm?: boolean
}

export type TravelRatePeriodDto = {
    // ISO dates (yyyy-MM-dd); either bound may be absent (open-ended period).
    validFrom?: string
    validTo?: string
    // Delivered as a raw JSON number by the settings endpoint.
    rate: number | string
}

const GlobalContext = createContext<GlobalContextType>(null)

const getter = (url: string) => () => ({ method: 'GET', url })

const isWildcard = (uri: string): boolean => uri.indexOf('*') >= 0

// Turn a SharePoint wildcard segment ('*', '*.docx', 'Brief*.docx') into an anchored,
// case-insensitive matcher: escape all regex specials, then let '*' match any run of characters.
const wildcardToRegExp = (pattern: string): RegExp => new RegExp(`^${pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*')}$`, 'i')

// Templates are identified and named by their base name; the extension is derived from the DocumentType.
const stripExtension = (name: string): string => {
    const dot = name.lastIndexOf('.')
    return dot > 0 ? name.substring(0, dot) : name
}

// Expand a single wildcard template into one concrete DocumentDto per matching file in its SharePoint folder.
// A missing/inaccessible folder must not break the whole /global load, so it simply contributes no templates.
async function expandWildcardDocument(axios: AxiosInstance, doc: DocumentDto, fallbackDriveId?: string): Promise<DocumentDto[]> {
    const driveId = doc.driveId ?? fallbackDriveId
    if (!driveId) {
        return []
    }

    const slash = doc.uri.lastIndexOf('/')
    const folderPath = slash >= 0 ? doc.uri.substring(0, slash) : ''
    const matcher = wildcardToRegExp(slash >= 0 ? doc.uri.substring(slash + 1) : doc.uri)
    const childrenUrl = folderPath ? `/drives/${driveId}/root:/${folderPath}:/children` : `/drives/${driveId}/root/children`

    try {
        const response = await axios.get<IGraphArrayPayload<DriveItem>>(childrenUrl, { baseURL: Destination.SharePoint })
        return (response.data.value ?? [])
            .filter(item => !item.folder && !!item.name) // real files only, never folders
            .filter(item => matcher.test(item.name))
            .map(item => ({
                ...doc,
                uri: folderPath ? `${folderPath}/${item.name}` : item.name,
                label: stripExtension(item.name),
                name: `${doc.name} - ${stripExtension(item.name)}`,
            }))
    } catch (error) {
        console.warn(`Failed to expand wildcard document '${doc.uri}':`, error)
        return []
    }
}

export function GlobalProvider(props: PropsWithChildren): ReactElement {
    // Re-fetch /global whenever the (user, environment) tuple changes. This is a defensive dependency.
    // See AuthorizationContext for the contract on `environmentKey`.
    const axios = useAxios()
    const { environmentKey } = useContext(AuthorizationContext)
    const { sharePoint } = useSharePoint()
    const [data, , resetSettings] = useEntity<GlobalContextType>(getter('/global'), false, undefined, undefined, undefined, [environmentKey])

    // Documents that already point at a concrete file need no expansion and can be exposed immediately.
    const staticDocuments = useMemo(
        () => (data.data?.settings?.documents ?? []).filter(doc => !isWildcard(doc.uri)),
        [data.data?.settings?.documents],
    )

    // Wildcard SHAREPOINT templates are resolved asynchronously to the real files they match.
    const [expandedDocuments, setExpandedDocuments] = useState<DocumentDto[]>([])

    useEffect(() => {
        const documents = data.data?.settings?.documents
        const wildcards = (documents ?? []).filter(doc => doc.storage === 'SHAREPOINT' && isWildcard(doc.uri))
        if (!documents || !environmentKey || wildcards.length === 0) {
            setExpandedDocuments([])
            return undefined
        }

        let cancelled = false
        Promise.all(wildcards.map(doc => expandWildcardDocument(axios, doc, sharePoint?.driveId))).then(results => {
            if (!cancelled) {
                setExpandedDocuments(results.flat())
            }
        })
        return () => {
            cancelled = true
        }
    }, [data.data?.settings?.documents, environmentKey, sharePoint?.driveId])

    // Consumers only ever see concrete files: the static documents plus the expanded wildcard matches.
    const documents = useMemo(() => [...staticDocuments, ...expandedDocuments], [staticDocuments, expandedDocuments])
    const settings = useMemo(
        () => (data.data?.settings ? { ...data.data.settings, documents } : data.data?.settings),
        [data.data?.settings, documents],
    )

    return (
        <GlobalContext.Provider
            value={{
                ...data.data,
                settings,
                loaded: data.loaded,
                reset: () => resetSettings(),
            }}
        >
            {props.children}
        </GlobalContext.Provider>
    )
}

export default GlobalContext
